import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    AGENT_SOFTWARE_ID,
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { START_DATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import ModalHeader from 'ui/Modals/ModalHeader';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import GVTHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/PayConstant/GVTHypotheses';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import AgentPayComponentHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/AgentPayComponent/AgentPayComponentHypotheses';
import GradeAdvancementHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/GradeAdvancement/GradeAdvancementHypotheses';
import NonRecurrentEnvelopes from '../NonRecurrentEnvelopes';
import EnvelopePayComponentHypotheses from '../EnvelopePayComponentHypotheses';
import { useGetObsoleteHypotheses } from './helpers/hooks/obsoleteHypotheses';
import DeparturesHypotheses from '../Departure/DeparturesHypotheses';
import ArrivalHypotheses from '../Arrival/ArrivalHypotheses';

type Props = {
    hrSimulation: HrSimulation;
    selectedOrgChartHrsSoftwareIds: string[];
    selectedEtablissementSoftwareIds: string[];
    isEtablissementsFilterActive: boolean;
    isPayBudgetFilterActive: boolean;
    selectedPayBudgetSoftwareIds: string[];
    setIsObsoleteHypothesesLoading: Dispatch<SetStateAction<boolean>>;
    simulationType: SimulationTypeEnum;
    recurrentInitialisationStartDate: SimulationDateString;
};

const StySkeleton = styled(Skeleton, {
    name: 'StySkeleton',
})(({ theme }) => ({
    br: '0.5rem',
    height: '5rem',
    backgroundColor: theme.palette.lightGrey,
}));

export default function ObsoleteHypotheses({
    hrSimulation,
    selectedOrgChartHrsSoftwareIds,
    selectedEtablissementSoftwareIds,
    isEtablissementsFilterActive,
    isPayBudgetFilterActive,
    selectedPayBudgetSoftwareIds,
    setIsObsoleteHypothesesLoading,
    simulationType,
    recurrentInitialisationStartDate,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const {
        data: obsoleteHypothesesResponse,
        isLoading: isObsoleteHypothesesLoading,
    } = useGetObsoleteHypotheses(
        hrSimulation.id,
        selectedOrgChartHrsSoftwareIds,
        isEtablissementsFilterActive,
        selectedEtablissementSoftwareIds,
        isPayBudgetFilterActive,
        selectedPayBudgetSoftwareIds,
        simulationType,
        recurrentInitialisationStartDate,
    );

    useEffect(() => {
        setIsObsoleteHypothesesLoading(isObsoleteHypothesesLoading);
    }, [isObsoleteHypothesesLoading, setIsObsoleteHypothesesLoading]);

    const detailedAgentDepartureHypothesesData =
        sortBy(obsoleteHypothesesResponse?.agentDepartureHypotheses, [
            ORG_CHART_HR_MAPPING,
            START_DATE,
        ]) || [];

    const detailedAgentArrivalHypothesesData =
        sortBy(obsoleteHypothesesResponse?.agentArrivalHypotheses, [
            ORG_CHART_HR_MAPPING,
            START_DATE,
        ]) || [];
    const envelopeMantyStatusHypothesesData =
        sortBy(obsoleteHypothesesResponse?.envelopeMantyStatusHypotheses, [
            MANTY_STATUS,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const envelopePayComponentHypothesesData =
        sortBy(obsoleteHypothesesResponse?.envelopePayComponentHypotheses, [
            MANTY_STATUS,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const agentPayComponentHypothesesData =
        sortBy(obsoleteHypothesesResponse?.agentPayComponentHypotheses, [
            AGENT_SOFTWARE_ID,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const detailedGradeAdvancementHypothesisData =
        sortBy(obsoleteHypothesesResponse?.gradeAdvancementHypotheses, [
            AGENT_SOFTWARE_ID,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const freeHypotheses = obsoleteHypothesesResponse?.freeHypotheses || [];

    const undefinedGvtHypothesis = {
        value: 0,
        defaultValue: 0,
        date: '',
    };

    const gvtHypothesesData = {
        indexPointHypothesis:
            obsoleteHypothesesResponse?.indexPointHypothesis ??
            undefinedGvtHypothesis,
        indexRaiseHypothesis:
            obsoleteHypothesesResponse &&
            Object.keys(
                // if the hypothesis is obsolete an empty object is returned
                obsoleteHypothesesResponse?.indexRaiseHypothesis,
            ).length
                ? obsoleteHypothesesResponse?.indexRaiseHypothesis
                : undefinedGvtHypothesis,
        rateCnraclHypothesis:
            obsoleteHypothesesResponse?.rateCnraclHypothesis ??
            undefinedGvtHypothesis,
        smicHypothesis:
            obsoleteHypothesesResponse?.smicHypothesis ??
            undefinedGvtHypothesis,
    };

    return (
        <>
            <ModalHeader
                title={t('humanResources.simulation.duplicateSimulation.title')}
                subtitle={t(
                    'humanResources.simulation.duplicateSimulation.secondStep.subtitle',
                )}
                description={t(
                    'humanResources.simulation.duplicateSimulation.secondStep.description',
                )}
            />
            {isObsoleteHypothesesLoading ? (
                <Box>
                    {Array.from({ length: 4 }).map((_a, index) => (
                        <StySkeleton key={index} variant="text" />
                    ))}
                </Box>
            ) : (
                <Box>
                    <GVTHypotheses
                        gvtHypotheses={gvtHypothesesData}
                        freeHypotheses={freeHypotheses}
                    />
                    <ArrivalHypotheses
                        detailedAgentArrivalHypothesesData={
                            detailedAgentArrivalHypothesesData
                        }
                    />
                    <DeparturesHypotheses
                        detailedAgentDepartureHypothesesData={
                            detailedAgentDepartureHypothesesData
                        }
                    />
                    <NonRecurrentEnvelopes
                        envelopeMantyStatusHypotheses={
                            envelopeMantyStatusHypothesesData
                        }
                    />
                    <EnvelopePayComponentHypotheses
                        hypotheses={envelopePayComponentHypothesesData}
                    />
                    <AgentPayComponentHypotheses
                        agentPayComponentHypotheses={
                            agentPayComponentHypothesesData
                        }
                    />
                    <GradeAdvancementHypotheses
                        detailedGradeAdvancementHypothesis={
                            detailedGradeAdvancementHypothesisData
                        }
                    />
                </Box>
            )}
        </>
    );
}
